import { useEffect, useMemo, useState } from 'react';
import Button from '../../../components/Button';
import {
    learningQuestionsStatsService,
    type OptionStat,
    type QuestionStats,
    type SectionStats,
} from '../services/learningQuestionsStatsService';
// TODO: uncomment once Firestore collection group index for ocd_profile is deployed
// import { onboardingStatsService } from '../services/onboardingStatsService';

const formatPercent = (value: number): string => `${Math.round(value * 100)}%`;

const OptionRow = ({ option }: { option: OptionStat }) => (
    <div className="mb-2">
        <div className="flex items-center justify-between text-sm mb-1">
            <span className="flex items-center gap-1 text-gray-800">
                {option.isCorrect && (
                    <span className="text-green-600" title="Correct answer">
                        ✓
                    </span>
                )}
                <span className={option.isCorrect ? 'font-semibold text-green-700' : ''}>
                    {option.label}
                </span>
            </span>
            <span className="text-gray-500 whitespace-nowrap ml-3">
                {option.count} ({formatPercent(option.percent)})
            </span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
            <div
                className={`h-2 rounded-full ${option.isCorrect ? 'bg-green-500' : 'bg-blue-500'}`}
                style={{ width: `${Math.min(option.percent * 100, 100)}%` }}
            />
        </div>
    </div>
);

const QuestionCard = ({ question }: { question: QuestionStats }) => (
    <div className="border border-gray-200 rounded-lg p-4">
        <div className="flex items-start justify-between gap-3 mb-3">
            <h4 className="text-sm font-semibold text-gray-900">{question.questionText}</h4>
            <span className="text-xs text-gray-500 whitespace-nowrap">
                {question.respondents} {question.respondents === 1 ? 'respondent' : 'respondents'}
            </span>
        </div>

        {question.respondents === 0 ? (
            <div className="text-sm text-gray-400 italic">No responses yet</div>
        ) : (
            <>
                {question.options.map((option, index) => (
                    <OptionRow key={index} option={option} />
                ))}
                {question.legacyAnswers.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-dashed border-gray-200">
                        <div className="text-xs text-gray-400 mb-2">
                            Answers not matching any current option (question edited since answered)
                        </div>
                        {question.legacyAnswers.map((option, index) => (
                            <OptionRow key={index} option={option} />
                        ))}
                    </div>
                )}
            </>
        )}
    </div>
);

export const QuestionsStats = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [sections, setSections] = useState<SectionStats[]>([]);
    const [onboardingQuestions, /* setOnboardingQuestions */] = useState<QuestionStats[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [hideEmpty, setHideEmpty] = useState(true);

    const handleFetch = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const [learningResult] = await Promise.all([
                learningQuestionsStatsService.getLearningQuestionStats(),
                // TODO: uncomment once Firestore collection group index for ocd_profile is deployed
                // onboardingStatsService.getOnboardingQuestionStats(),
            ]);
            setSections(learningResult);
            // setOnboardingQuestions(onboardingResult);
        } catch (err) {
            console.error('Error fetching question stats:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch question stats');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        handleFetch();
    }, []);

    const visibleOnboardingQuestions = useMemo(() => {
        if (!hideEmpty) return onboardingQuestions;
        return onboardingQuestions.filter((q) => q.respondents > 0);
    }, [onboardingQuestions, hideEmpty]);

    const visibleSections = useMemo(() => {
        if (!hideEmpty) return sections;
        return sections
            .map((section) => ({
                ...section,
                lessons: section.lessons
                    .map((lesson) => ({
                        ...lesson,
                        questions: lesson.questions.filter((q) => q.respondents > 0),
                    }))
                    .filter((lesson) => lesson.questions.length > 0),
            }))
            .filter((section) => section.lessons.length > 0);
    }, [sections, hideEmpty]);

    if (isLoading) {
        return (
            <div className="bg-white rounded-lg shadow-sm p-6 mt-6">
                <div className="flex items-center justify-center py-12">
                    <div className="text-gray-500">Loading question stats...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white rounded-lg shadow-sm p-6 mt-6">
                <div className="flex flex-col items-center justify-center py-12">
                    <div className="text-red-600 mb-4">{error}</div>
                    <Button onClick={handleFetch} variant="primary">
                        Retry
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mt-6">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Questions</h2>
                <div className="flex flex-wrap items-center gap-4">
                    <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={hideEmpty}
                            onChange={(e) => setHideEmpty(e.target.checked)}
                        />
                        Hide questions with no responses
                    </label>
                    <Button onClick={handleFetch} variant="primary">
                        Refresh
                    </Button>
                </div>
            </div>

            {/* Onboarding Questions */}
            {visibleOnboardingQuestions.length > 0 && (
                <div className="mb-8">
                    <h3 className="text-lg font-bold text-gray-900 border-b border-gray-200 pb-2 mb-4">
                        Onboarding Questions
                    </h3>
                    <div className="space-y-4">
                        {visibleOnboardingQuestions.map((question) => (
                            <QuestionCard key={question.questionUid} question={question} />
                        ))}
                    </div>
                </div>
            )}

            {/* Learning Questions */}
            {visibleSections.length === 0 && visibleOnboardingQuestions.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                    No question responses found.
                </div>
            ) : (
                visibleSections.length > 0 && (
                    <div className="space-y-8">
                        <h3 className="text-lg font-bold text-gray-900 border-b border-gray-200 pb-2">
                            Learning Questions
                        </h3>
                        {visibleSections.map((section) => (
                            <div key={section.sectionUid}>
                                <h4 className="text-base font-bold text-gray-800 border-b border-gray-100 pb-2 mb-4">
                                    {section.title}
                                </h4>
                                <div className="space-y-6">
                                    {section.lessons.map((lesson) => (
                                        <div key={lesson.lessonUid}>
                                            <div className="flex flex-wrap items-center gap-2 mb-3">
                                                <h5 className="text-sm font-semibold text-gray-700">
                                                    {lesson.title}
                                                </h5>
                                                <span
                                                    className={`text-xs px-2 py-0.5 rounded-full ${
                                                        lesson.type === 'quiz'
                                                            ? 'bg-purple-100 text-purple-700'
                                                            : 'bg-gray-100 text-gray-600'
                                                    }`}
                                                >
                                                    {lesson.type}
                                                </span>
                                            </div>
                                            <div className="space-y-4">
                                                {lesson.questions.map((question) => (
                                                    <QuestionCard
                                                        key={question.questionUid}
                                                        question={question}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )
            )}
        </div>
    );
};
